import { v4 as UUIDv4 } from 'uuid';
import clone from 'lodash/clone';
import { getImageOssPath, getRemoteImageSize } from '@shakkerai/util-image';
import { getVipState } from '@shakkerai/pro-store';
import { compressRemoteImage, isOverseas, isTest } from '@shakkerai/pro-utils';
import { copyOSSImage, MAX_UPLOAD_SIZE } from '@shakkerai/pro-utils';
import { applyDerivativeResults } from '../../derivative';
import { outpaintPresetModels } from '@shakkerai/pro-models';

const testCheckpointId = 18511045;
const xingliuCheckpointId = 18532056;
const shakkerCheckpointId = 18511404;

export const outpaintPowerParamsHandler = (params) => {
  const tempReferenceImagePath = 'copy/sd-images/2daf83d29cec1eacbaa1545a110179a180612c93bff7d9f1f150e62001a87f5e.png';
  const baseParams = {
    adetailerEnable: 0,
    mode: 1,
    uuid: UUIDv4(),
    vae: '',
    taskQueuePriority: getVipState('vipAccelerate') ? 2 : 1, // 1 普通队列 2加速队列
  };

  let prompt = params.prompt?.trim() || '';
  let negativePrompt = params.negativePrompt?.trim() || '';

  // 去除提示词中的引号、大于号、小于号
  prompt = prompt?.replace(/['"<>]/g, '') || '';
  negativePrompt = negativePrompt?.replace(/['"<>]/g, '') || '';

  const { relativeXY } = params?.outpaintRect ?? {};
  const canvas = { ...relativeXY };
  const { checkpointId } = params;

  canvas.left = canvas.left - (canvas.left % 8);
  canvas.right = canvas.right - (canvas.right % 8);
  canvas.bottom = canvas.bottom - (canvas.bottom % 8);
  canvas.top = canvas.top - (canvas.top % 8);
  const powerPaintUserParam = {
    checkpointId: isTest ? testCheckpointId : isOverseas ? shakkerCheckpointId : xingliuCheckpointId,
    sourceImageId: tempReferenceImagePath,
    prompt: params.prompt,
    repeatLatentBatch: 1,
    canvas: canvas,
  };

  baseParams.comfyParams = {
    internalComfyWorkflowType: 0,
    powerPaintUserParam: powerPaintUserParam,
  };

  return baseParams;
};

export const outpaintParamsHandler = async ({ params, isTryClacMode = false }) => {
  if (isTryClacMode) {
    return outpaintPowerParamsHandler(params);
  }
  const { outpaintRect, checkpointId } = params;
  const { sourceElement } = params;

  const baseParams = {
    adetailerEnable: 0,
    mode: 1,
    uuid: UUIDv4(),
    projectData: clone(params),
    vae: '',
    taskQueuePriority: getVipState('vipAccelerate') ? 2 : 1, // 1 普通队列 2加速队列
  };

  baseParams.projectData.mode = 1;
  baseParams.projectData.generateType = 'outpaint';
  baseParams.projectData.baseType = 19;

  let prompt = params.prompt?.trim() || '';
  let negativePrompt = params.negativePrompt?.trim() || '';

  // 去除提示词中的引号、大于号、小于号
  prompt = prompt?.replace(/['"<>]/g, '') || '';
  negativePrompt = negativePrompt?.replace(/['"<>]/g, '') || '';

  baseParams.projectData.prompt = prompt;
  baseParams.projectData.negativePrompt = negativePrompt;

  // 核心生图参数，传给txt2img或者img2img的
  const generateParams = {
    width: params.width,
    height: params.height,
    prompt: prompt,
    negativePrompt: negativePrompt,
    samplingMethod: params.samplerMethod || '15',
    samplingStep: params.samplingSteps,
    // hiresOptions: { ...params.hiresOptions },
    imgCount: params.count,
    batchSize: 1,
    batchCount: 1,
    cfgScale: params.cfgScale,
    clipSkip: params.clipSkip,
    seed: -1,
    tiling: 0,
    seedExtra: 0,
    restoreFaces: 0,
    hiResFix: 0,
    extraNetwork: [],
  };

  baseParams.projectData.sourceElement = {
    id: UUIDv4(),
    url: sourceElement?.url,
    sourceId: sourceElement?.id,
    innerTaskId: sourceElement?.innerTaskId,
    innerImageId: sourceElement?.innerImageId,
    width: sourceElement?.width,
    height: sourceElement?.height,
  };

  baseParams.projectData.outpaintData = outpaintRect;

  generateParams.promptRecommend = false;

  // 提示词处理
  // const originalPrompt = generateParams.prompt;
  generateParams.prompt = generateParams.prompt?.replace(/^,+/, '') || '';
  generateParams.negativePrompt = generateParams.negativePrompt?.replace(/^,+/, '') || '';
  generateParams.seed = params.seedNumber ? Number(params.seedNumber) : -1;
  generateParams.width = Math.round(generateParams.width);
  generateParams.height = Math.round(generateParams.height);

  let { width, height } = generateParams;

  // 这里虽然用width、height做了兼容，正常不应该走到兼容，不然就是数据异常
  if (!outpaintRect.wrapper) {
    console.log('数据异常', outpaintRect);
  }
  baseParams.projectData.renderWidth = outpaintRect.wrapper?.width ?? width;
  baseParams.projectData.renderHeight = outpaintRect.wrapper?.height ?? height;

  // 最后统一尝试修正一下尺寸为8或者64的倍数
  // 这是个比较高风险的处理哦，因为有可能会导致生成结果和预期不一致

  const MOD_UNIT = 8; // 后来inpaint也由64改为8了
  const restWidth = generateParams.width % MOD_UNIT;
  const restHeight = generateParams.height % MOD_UNIT;

  if (restWidth !== 0) {
    generateParams.width = generateParams.width - restWidth + MOD_UNIT;
  }

  if (restHeight !== 0) {
    generateParams.height = generateParams.height - restHeight + MOD_UNIT;
  }
  generateParams.imageMode = 0;
  generateParams.denoisingStrength = 0.85;
  generateParams.width = Math.round(outpaintRect.wrapper.width);
  generateParams.height = Math.round(outpaintRect.wrapper.height);
  const relativeXY = outpaintRect.relativeXY ?? {};
  const canvas = { ...relativeXY };

  let referenceImage = sourceElement;
  let tempReferenceImagePath = null;

  if (referenceImage && referenceImage.url) {
    const remoteSize = await getRemoteImageSize(referenceImage.url);

    if (remoteSize.width > MAX_UPLOAD_SIZE || remoteSize.height > MAX_UPLOAD_SIZE) {
      try {
        const compressUrl = await compressRemoteImage(referenceImage.url, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE, true);
        referenceImage = {
          ...referenceImage,
          isTemp: true,
          width: MAX_UPLOAD_SIZE,
          height: MAX_UPLOAD_SIZE,
          url: compressUrl,
        };
      } catch (error) {
        console.log(error);
      }
    }
    if (referenceImage.isTemp) {
      tempReferenceImagePath = getImageOssPath(referenceImage.url);
    } else {
      tempReferenceImagePath = await copyOSSImage(
        getImageOssPath(referenceImage.url),
        true,
        isOverseas ? undefined : '3',
      )
        .then((res) => res.url)
        .then(getImageOssPath);
    }
  }

  canvas.left = canvas.left - (canvas.left % 8);
  canvas.right = canvas.right - (canvas.right % 8);
  canvas.bottom = canvas.bottom - (canvas.bottom % 8);
  canvas.top = canvas.top - (canvas.top % 8);

  const powerPaintUserParam = {
    checkpointId: isTest ? testCheckpointId : isOverseas ? shakkerCheckpointId : xingliuCheckpointId,
    sourceImageId: tempReferenceImagePath,
    prompt: params.prompt,
    repeatLatentBatch: 1,
    canvas: canvas,
  };

  generateParams.comfyParams = {
    internalComfyWorkflowType: 0,
    powerPaintUserParam: powerPaintUserParam,
  };

  baseParams.projectData.outpaintRect = { ...outpaintRect };

  if (generateParams.comfyParams) {
    baseParams.comfyParams = generateParams.comfyParams;
    baseParams.comfyParams.projectData = baseParams.projectData;
    baseParams.comfyParams.taskQueuePriority = baseParams.taskQueuePriority;
  }

  return baseParams;
};

export const outpaintResultHandler = async (taskData, results) => {
  // 如果要做一些别的操作，可以在这里处理
  // ...

  // 通用的衍生生图结果处理方式
  return applyDerivativeResults('outpaint', taskData, results);
};
